import { EventMediaEditor } from ".";
import _ from "lodash";
import * as moment from "moment";
import * as React from "react";
import * as BS from "react-bootstrap";
import * as Comp from "../../../../components";
import { ComponentWithValidatedForm } from "../../../../components/validation";
import { Statuses } from "../../../../domain";
import * as Forms from "../../../../forms";
import * as M from "../../../../models";
import { EventLevels, EventTypes } from "../../../../models/event";
import { Api } from "../../../../services/Api";
import { DateHelper, ReactHelper, Utility } from "../../../../utils";

interface OwnProps {
    api: Api;
    eventId: number;
    onClosed: () => void;
    onProcessing: () => void;
    onUpdated: (evt: M.Event.SubEvent) => void;
}

interface OwnState {
    abstractSelectOpen: boolean;
    event?: M.Event.SubEvent;
    show: boolean;
}

export class EventEditModal extends ComponentWithValidatedForm<OwnProps, OwnState> {

    private _searchTimeout: NodeJS.Timer;

    public constructor(props: any) {
        super(props);

        const formModel = new Forms.AdminEvent.SubEvent();
        this.state = {
            ...this.getInitialState(formModel),
            abstractSelectOpen: false,
            show: false,
        };
    }

    public close() {
        this.setState({
            ...this.state,
            event: undefined,
            show: false,
        }, () => {
            this.props.onClosed();
        });
    }

    public show(parent: M.Event.IEvent, evt?: M.Event.SubEvent) {
        const formModel = new Forms.AdminEvent.SubEvent();

        // don't reset the model if the event is already set
        // (such as when data is re-fetched automatically)
        if (this.state.show && this.state.event && evt && this.state.event.id === evt.id)
            return;

        if (evt) {
            formModel.data.active = Statuses.isActive(evt.statusId),
            formModel.data.description = evt.description || "";
            formModel.data.endDate = Utility.fromApiDate(evt.endDate) || null;
            formModel.data.id = evt.id;
            formModel.data.isSession = EventLevels.isSession(evt);
            formModel.data.locationName = evt.locationName || "";
            formModel.data.media = evt.documents.map((doc, i) => ({
                index: i,
                isDeleted: false,
                isFloorPlan: doc.isFloorPlan,
                media: {
                    fileName: doc.media.fileName,
                    guid: doc.media.guid,
                    id: doc.media.id,
                    size: doc.media.size,
                    url: doc.media.url,
                },
            })),
            formModel.data.parentId = evt.parentId || 0;
            formModel.data.rsvp = {
                allowTableSelection: evt.allowTableSelection,
                canRsvp: evt.canRsvp,
                canRsvpGuests: evt.canRsvpGuests,
                deadline: Utility.fromApiDate(evt.rsvpDeadline) || null,
                // seatingChartUrl: evt.rsvpSeatingChartUrl,
                seatsAvailable: evt.seatsAvailable,
                seatsPerTable: evt.seatsPerTable,
            },
            formModel.data.startDate = Utility.fromApiDate(evt.startDate) || null;
            formModel.data.title = evt.title;
            formModel.data.typeId = evt.typeId;

            if (evt.scheduledAbstract)
                formModel.data.scheduledAbstract = {
                    ...evt.scheduledAbstract,
                    label: evt.title,
                    value: evt.scheduledAbstract.id,
                };

            if (evt.parentId !== parent.id)
                throw new Error(`Event parent ID does not match given parent ID (${evt.parentId} vs. ${parent.id})`);
        }
        else {
            formModel.data.parentId = parent.id;
            formModel.data.isSession = EventLevels.isSession((parent.levelId || EventLevels.MainEvent.id) + 1);

            // try to be smart about pre-setting start date
            if (parent.levelId === EventLevels.SubEvent.id) {
                const subEvent = parent as M.Event.SubEvent;
                if (subEvent.children && subEvent.children.length) {
                    // if there are sessions in an event, set start date to the end date of last session
                    const maxEndDate = moment(_.max(parent.children.map((x) => (Utility.fromApiDate(x.endDate) as Date).valueOf())));
                    formModel.data.startDate = maxEndDate.toDate();
                }
                else
                    formModel.data.startDate = Utility.fromApiDate(parent.startDate) || null;

                formModel.data.endDate = new Date((formModel.data.startDate as Date).valueOf());
            }
            else if (parent.levelId === EventLevels.MainEvent.id) {
                formModel.data.startDate = new Date(parent.startDate.valueOf());
                formModel.data.endDate = new Date(parent.startDate.valueOf());
            }
        }

        this.setState({ ...this.state,
            ...this.getInitialState(formModel),
            abstractSelectOpen: formModel.data.id === 0 && formModel.data.isSession,
            event: evt,
            show: true,
        });
    }

    public render() {
        const evt = this.state.event;
        const formModel = this.getFormModelData<Forms.AdminEvent.SubEventData>();
        const evtCat = formModel.isSession ? "Session" : "Event";

        const rsvpFields = formModel.rsvp.canRsvp
            ? <>
                <Comp.Editors.CheckboxField formComponent={this} name="rsvp.canRsvpGuests" />
                <Comp.Editors.CheckboxField formComponent={this} name="rsvp.allowTableSelection" />
                <Comp.Editors.DateAndTimePickerField formComponent={this} name="rsvp.deadline" />
                <Comp.Editors.NumberField formComponent={this} name="rsvp.seatsAvailable" />
                {formModel.rsvp.allowTableSelection
                    ? <Comp.Editors.NumberField formComponent={this} name="rsvp.seatsPerTable" required />
                    : null}
            </>
            : null;

        let scheduledAbstractEditor: JSX.Element | null = null;
        if (formModel.isSession) {
            const scheduledAbstract = formModel.scheduledAbstract as M.Abstract.ScheduledAbstractItem;
            scheduledAbstractEditor = <Comp.Editors.FormField name="" label="Scheduled abstract">
                <BS.Collapse in={!this.state.abstractSelectOpen}>
                    <p>
                        {scheduledAbstract
                            ? this.renderAbstractDetails(scheduledAbstract)
                            : "None selected"}
                        <br />
                        <span className="mute">
                            <a href="#" onClick={(e) => { ReactHelper.emptyClick(e); this.toggleAbstractSelection(); }}>
                                Choose {scheduledAbstract ? "a different" : "an"} abstract
                            </a>
                        </span>
                    </p>
                </BS.Collapse>
                <BS.Collapse in={this.state.abstractSelectOpen}>
                    <div>
                        <Comp.Editors.SelectField
                            formComponent={this}
                            isAsync
                            name="scheduledAbstract"
                            noFieldWrap
                            select={{
                                // cache: false,
                                ignoreCase: false,
                                loadOptions: (search: string) => this.searchAbstracts(search),
                                optionRenderer: (x: M.Abstract.ScheduledAbstractItem) => <p>{this.renderAbstractDetails(x)}</p>,
                                searchPromptText: "Start typing and select from suggested abstracts",
                                valueRenderer: (x: M.Abstract.ScheduledAbstractItem) => <p dangerouslySetInnerHTML={{ __html: x.title }} />,
                            }}
                        />
                        <span className="mute">
                            <a href="#" onClick={(e) => { ReactHelper.emptyClick(e); this.toggleAbstractSelection(); }}>
                                Cancel
                            </a>
                        </span>
                    </div>
                </BS.Collapse>
            </Comp.Editors.FormField>;
        }

        const types: M.Editors.SelectItem[] = EventTypes.SubEventTypes.map((v) => ({
            id: v.id,
            render: v.name,
        }));

        const typeEditor = formModel.isSession
            ? null
            : <Comp.Editors.DropdownField formComponent={this} name="typeId" items={types} css="txt" placeholder="Select event type (optional)" />;

        return <Comp.Modal.ModalForm
            formComponent={this}
            onCancel={() => this.close()}
            processing={this.state.formProcessing}
            show={this.state.show}
            title={evt ? `Edit ${evt.title}` : `Add ${evtCat.toLowerCase()}`}
        >

            <Comp.Validation.ValidatedForm
                formComponent={this}
                model={this.state.formModel.data}
                onInputChange={(inputName) => this.handleInputChange(inputName)}
                onValidSubmit={(model) => this.saveEvent(model)}
                schema={this.state.formModel.rules}
                ref={(ref: any) => this.form = ref}
            >

                <Comp.Editors.CheckboxField formComponent={this} name="active" />
                <Comp.Editors.TextField formComponent={this} name="title" css="txt" />
                {typeEditor}
                <Comp.Editors.DateAndTimePickerField formComponent={this} name="startDate" />
                <Comp.Editors.DateAndTimePickerField formComponent={this} name="endDate" />
                {scheduledAbstractEditor}
                <Comp.Editors.RichTextField formComponent={this} name="description" options={{ height: "150px" }} />
                <Comp.Editors.TextField formComponent={this} name="locationName" css="txt" />
                <EventMediaEditor api={this.props.api} eventId={this.props.eventId} formComponent={this} />

                <Comp.Editors.CheckboxField formComponent={this} name="rsvp.canRsvp" label={`Registrants can RSVP to this ${evtCat.toLowerCase()}`} />
                {rsvpFields}

            </Comp.Validation.ValidatedForm>
        </Comp.Modal.ModalForm>;
    }

    private handleInputChange(inputName: string) {
        if (inputName === "scheduledAbstract") {
            const scheduledAbstract = this.getFieldValue("scheduledAbstract") as M.Abstract.ScheduledAbstractItem;
            if (scheduledAbstract) {
                const formModel = this.getFormModelData<Forms.AdminEvent.SubEventData>();
                formModel.title = scheduledAbstract.title;

                if (scheduledAbstract.defaultDuration && formModel.startDate)
                    formModel.endDate = moment(formModel.startDate).add(scheduledAbstract.defaultDuration, "minutes").toDate();

                this.updateFormModelData(formModel, () => {
                    this.toggleAbstractSelection();
                });
            }
        }
    }

    private renderAbstractDetails(x: M.Abstract.ScheduledAbstractItem) {
        return <>
            #{x.id} - <span dangerouslySetInnerHTML={{ __html: x.title }} />
            <br />
            <span className="mute">
                {x.typeShortName}{x.placement || x.classification ? " - " : ""}
                {Utility.joinSkipEmpty([x.placement, x.classification], ", ")}{" by "}
                <strong>{Utility.joinSkipEmpty(x.primaryAuthors.map((author) => M.Generic.PersonHelper.getFullNameWithSuffix(author)), ", ")}</strong>
            </span>
        </>;
    }

    private async saveEvent(model: Forms.AdminEvent.SubEventData) {
        this.toggleProcessing(true);

        const body = {
            active: model.active,
            allowTableSelection: model.rsvp.allowTableSelection,
            canRsvp: model.rsvp.canRsvp,
            canRsvpGuests: model.rsvp.canRsvpGuests,
            description: model.description,
            endDate: DateHelper.formatDateWithoutZone(model.endDate || new Date()),
            id: model.id,
            locationName: model.locationName,
            media: model.media
                .filter((x) => !x.isDeleted && x.media !== null)
                .map((x) => ({
                    guid: x.media!.guid,
                    isFloorPlan: x.isFloorPlan,
                })),
            parentId: model.parentId,
            rsvpDeadline: model.rsvp.deadline
                ? moment(model.rsvp.deadline || new Date()).format()
                : null,
            scheduledAbstractId: model.isSession && model.scheduledAbstract
                ? (model.scheduledAbstract as M.Abstract.ScheduledAbstractItem).id
                : null,
            seatsAvailable: model.rsvp.seatsAvailable,
            seatsPerTable: model.rsvp.seatsPerTable,
            startDate: DateHelper.formatDateWithoutZone(model.startDate || new Date()),
            title: model.title,
            typeId: model.typeId,
        };

        const result = await this.props.api.post(Api.endpoints.admin.event.subEvent.withUrlParams({ eventId: model.parentId }), {
            body: JSON.stringify(body),
        });

        if (result.isSuccess && result.data) {
            this.props.onUpdated(result.data);
            this.close();
        }
        else {
            this.setFormErrors(M.Result.Result.errorsOrMessage(result), undefined, true);
        }

        this.toggleProcessing(false);
    }

    private searchAbstracts(search: string) {
        if (!search || search.length < 2)
            return Promise.resolve({ options: [] });

        return new Promise((resolve, reject) => {
            // delay the ajax call as to not make a call for each letter typed.
            if (this._searchTimeout !== undefined)
                clearTimeout(this._searchTimeout);

            this._searchTimeout = setTimeout(() => {
                const queryParams = { queryParams: {
                    eventId: this.props.eventId,
                    q: search,
                    scheduled: false,
                    statusIds: M.Abstract.AbstractStatuses.Accepted.id,
                }};

                this.props.api.callApi(Api.endpoints.admin.abstract.search, queryParams)
                    .then((result) => {
                        if (result.isSuccess && result.data) {
                            let options = result.data.map((x) => {
                                return Utility.toReactSelectOption(x, x.title, x.id);
                            });

                            options = [...options];
                            resolve({ options });
                        }
                        else
                            console.error(result.errors.join("; "));
                    });
            }, 375);
        }).then((data) => {
            return data;
        });
    }

    private toggleAbstractSelection() {
        this.setState({ ...this.state, abstractSelectOpen: !this.state.abstractSelectOpen });
    }
}



// WEBPACK FOOTER //
// ./src/containers/adminEvent/editCalendar/components/EventEditModal.tsx